import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import { playNotificationSound } from '../utils/notificationSound';
import { BACKEND_URL } from '../utils/config';
import useMessagesStore from '../stores/useMessagesStore';
import useNotificationsStore from '../stores/useNotificationsStore';
import { safeLocalStorage } from '../utils/safeStorage';
import { showBrowserNotification } from '@/utils/browserNotification';
import { authError } from '@/utils/authError';
import { isHiddenFor } from '@/utils/hiddenFor';

const SocketContext = createContext(null);

// Store logout function globally to avoid circular dependency
let globalLogoutFn = null;
export const setGlobalLogout = (fn) => { globalLogoutFn = fn; };

// Constants for reconnection and polling
const RECONNECT_INTERVAL_MS = 3000;
// Never give up while the page is open: after a cap, an open chat stayed deaf
// until the tab was hidden and shown again (mobile does the same, BUG-515).
const MAX_RECONNECT_ATTEMPTS = Infinity;
const POLLING_INTERVAL_MS = 60000; // Fallback polling every 60s
// Note: socket.io's protocol-level PING/PONG (engine.io) handles
// liveness automatically via the server's `ping_timeout` /
// `ping_interval` settings. Application-layer heartbeat removed.

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { token, isAuthenticated, user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState({});
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // 'disconnected' | 'connecting' | 'connected' | 'reconnecting'
  // Bumped by the lifecycle effect whenever a brand-new socket is created.
  // The listener-binding effect uses this in its dep list so it knows to
  // (re-)attach handlers to the new socket instance. Keeps lifecycle
  // and listener-wiring in separate effects (canonical Socket.IO/React
  // pattern — see https://socket.io/how-to/use-with-react).
  const [socketCreatedTick, setSocketCreatedTick] = useState(0);

  // Use refs to persist across re-renders
  const socketRef = useRef(null);
  const notificationCallbacks = useRef([]);
  const messageCallbacks = useRef([]);
  const readReceiptCallbacks = useRef([]);
  const messageEditedCallbacks = useRef([]);
  const messageDeletedCallbacks = useRef([]);
  // Subscribers to `call_ended` — fired when EITHER participant ends a
  // scheduled audio call. Used by CallScreen to force-exit the other
  // side reliably (LiveKit room-delete propagation alone has been
  // unreliable in practice).
  const callEndedCallbacks = useRef([]);
  // Subscribers to `call_status_changed` — fired when a scheduled call
  // transitions state mid-flight (e.g. confirmed → in_progress when one
  // side mints a token / joins the room). Lets ScheduledCallCard refresh
  // in real time on the OTHER party's screen without a manual refresh.
  const callStatusChangedCallbacks = useRef([]);
  const messageReactionCallbacks = useRef([]);
  const matchEventCallbacks = useRef(new Set());
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const activeConversationIdRef = useRef(null);
  // Live presence (user_id → { is_online, last_seen }) — populated from the
  // backend's user_activity_update event so chat headers refresh without
  // a full conversation refetch.
  const [userActivity, setUserActivity] = useState({});

  // Zustand store actions
  const fetchConversations = useMessagesStore(state => state.fetchConversations);
  const fetchUnreadCount = useMessagesStore(state => state.fetchUnreadCount);
  const handleNewMessageStore = useMessagesStore(state => state.handleNewMessage);
  const handleOwnMessageStore = useMessagesStore(state => state.handleOwnMessage);
  const removeConversation = useMessagesStore(state => state.removeConversation);
  const markConversationAsRead = useMessagesStore(state => state.markConversationAsRead);
  const resetMessagesStore = useMessagesStore(state => state.reset);

  const fetchNotifications = useNotificationsStore(state => state.fetchNotifications);
  const handleNewNotificationStore = useNotificationsStore(state => state.handleNewNotification);
  const resetNotificationsStore = useNotificationsStore(state => state.reset);

  // Get the active token
  const getActiveToken = useCallback(() => {
    return token;
  }, [token]);

  // Get current user ID from token
  const getCurrentUserId = useCallback(() => {
    // The signed-in user first: with a cookie session there is no token in
    // memory, and a null here dropped every incoming message on the floor
    // for a member who came back to the site (BUG-530).
    if (user?.id) return user.id;
    const activeToken = getActiveToken();
    if (!activeToken) return null;
    try {
      const payload = JSON.parse(atob(activeToken.split('.')[1]));
      return payload.user_id || payload.sub;
    } catch (e) {
      return null;
    }
  }, [user?.id, getActiveToken]);

  // Check if authenticated
  const hasAuth = isAuthenticated;

  /**
   * Fetch all data from API (used for initial load and polling fallback)
   */
  const fetchAllData = useCallback(() => {
    // Signed in is the condition, not "holds a token in memory": the session
    // is the httpOnly cookie, and `token` is only set by a sign-in in THIS
    // tab. Gated on it, a member who came back to the site never loaded her
    // conversations or notifications here, and both header bells said
    // "nothing new" while she had unread messages (BUG-530).
    if (!hasAuth) return;

    fetchConversations();
    fetchNotifications();
  }, [hasAuth, fetchConversations, fetchNotifications]);

  /**
   * Fire all match event callbacks (for real-time request/conversation refresh)
   */
  const fireMatchEventCallbacks = useCallback(() => {
    matchEventCallbacks.current.forEach(cb => {
      try { cb(); } catch (e) { /* Callback error handled silently */ }
    });
  }, []);

  /**
   * Start polling fallback
   */
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      // Only poll if socket is not connected
      if (!socketRef.current?.connected) {
        console.log('[Socket] Polling fallback triggered');
        fetchAllData();
      }
    }, POLLING_INTERVAL_MS);
  }, [fetchAllData]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  /**
   * Handle visibility change (tab focus)
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && hasAuth) {
        console.log('[Socket] Tab became visible, refreshing data...');

        // Refresh data immediately
        fetchAllData();

        // If socket disconnected, try to reconnect
        if (socketRef.current && !socketRef.current.connected) {
          socketRef.current.connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [hasAuth, fetchAllData]);

  /**
   * Manual reconnect function
   */
  const reconnect = useCallback(() => {
    if (socketRef.current) {
      setConnectionStatus('reconnecting');
      socketRef.current.connect();
    }
  }, []);

  // ============================================================
  // EFFECT 1 — Socket lifecycle (create on auth, destroy on logout/unmount)
  //
  // Deps intentionally minimal: `[hasAuth]` (+ a handful of stable Zustand
  // store actions and useCallback wrappers that never actually change in
  // practice). Token rotations and changing handler closures do NOT
  // rebuild the socket — Effect 2 (listeners) and Effect 3 (re-emit
  // authenticate) handle those independently.
  //
  // Pattern reference: https://socket.io/how-to/use-with-react
  //   "have no dependency for this Effect in order to prevent a
  //    reconnection on each render."
  // ============================================================
  useEffect(() => {
    if (!hasAuth) {
      // Teardown — user logged out (or was never logged in).
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
      setAuthenticated(false);
      setConnectionStatus('disconnected');
      stopPolling();
      resetMessagesStore();
      resetNotificationsStore();
      return;
    }

    // Setup — user is authenticated, create the socket instance.
    setConnectionStatus('connecting');
    const newSocket = io(BACKEND_URL, {
      // Send the httpOnly access_token cookie with the WS handshake so
      // the backend can authenticate the connection without us ever
      // exposing the token to JS. Without this the web client could only
      // authenticate during the brief window right after a fresh login
      // (when setToken puts a copy in memory) — a page refresh wipes
      // memory but leaves the cookie, and the socket would silently sit
      // unauthenticated until the user logged in again. Mobile is
      // unaffected: the Flutter client passes the token via the `auth`
      // field, which the backend checks first.
      withCredentials: true,
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: RECONNECT_INTERVAL_MS,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
      transports: ['websocket', 'polling'],
      timeout: 20000,
    });
    socketRef.current = newSocket;

    // Bump the tick → triggers Effect 2 to bind listeners on this new
    // socket. React commits both effects in the same render cycle, well
    // before the network handshake completes — so listeners are bound
    // before any `connect` event can fire.
    setSocketCreatedTick((t) => t + 1);

    // Polling fallback runs independently of socket connection state.
    startPolling();

    return () => {
      // Runs on hasAuth flipping false OR component unmount.
      newSocket.disconnect();
      if (socketRef.current === newSocket) {
        socketRef.current = null;
      }
      stopPolling();
    };
  }, [hasAuth, stopPolling, startPolling, resetMessagesStore, resetNotificationsStore]);

  // ============================================================
  // EFFECT 2 — Listener binding (canonical Socket.IO/React pattern)
  //
  // Re-runs when:
  //   - A new socket is created (socketCreatedTick bumps), OR
  //   - Any handler closure dep changes (e.g. token-rotation regenerates
  //     getActiveToken / getCurrentUserId / fetchAllData).
  //
  // Every `socket.on(name, handler)` is paired with a matching
  // `socket.off(name, handler)` in cleanup, using NAMED handlers — so we
  // remove only OUR listener, never another component's. Per Socket.IO
  // official guide: "Any event listeners registered in the setup function
  // must be removed in the cleanup callback in order to prevent duplicate
  // event registrations."
  // ============================================================
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    // ----- Named handlers -----
    const handleConnect = () => {
      console.log('[Socket] Connected');
      setConnected(true);
      setConnectionStatus('connected');
      reconnectAttemptsRef.current = 0;
      const authToken = getActiveToken();
      if (authToken) {
        socket.emit('authenticate', { token: authToken });
      }
      fetchAllData();
    };

    const handleAuthenticated = (data) => {
      console.log('[Socket] Authenticated');
      setAuthenticated(true);
      if (data?.user_id) {
        socket.emit('join', data.user_id);
      }
    };

    const handleDisconnect = (reason) => {
      console.log('[Socket] Disconnected:', reason);
      setConnected(false);
      setAuthenticated(false);
      setConnectionStatus('disconnected');
      if (reason === 'io server disconnect') {
        // Server kicked us — try a manual reconnect after the delay.
        setTimeout(() => {
          if (hasAuth) {
            socket.connect();
          }
        }, RECONNECT_INTERVAL_MS);
      }
    };

    const handleReconnectAttempt = (attemptNumber) => {
      console.log('[Socket] Reconnect attempt:', attemptNumber);
      setConnectionStatus('reconnecting');
      reconnectAttemptsRef.current = attemptNumber;
    };

    const handleReconnect = () => {
      console.log('[Socket] Reconnected');
      setConnectionStatus('connected');
      reconnectAttemptsRef.current = 0;
      fetchAllData();
    };

    const handleReconnectFailed = () => {
      console.log('[Socket] Reconnect failed, falling back to polling');
      setConnectionStatus('disconnected');
    };

    const handleConnectError = (error) => {
      console.log('[Socket] Connection error:', error?.message);
      setConnectionStatus('disconnected');
    };

    const handleUserTyping = ({ user_id, conversation_id }) => {
      setTypingUsers((prev) => ({ ...prev, [conversation_id]: user_id }));
    };

    const handleUserStoppedTyping = ({ conversation_id }) => {
      setTypingUsers((prev) => {
        const updated = { ...prev };
        delete updated[conversation_id];
        return updated;
      });
    };

    const handleUserOnline = ({ user_id }) => {
      setOnlineUsers((prev) => ({ ...prev, [user_id]: true }));
    };

    const handleUserOffline = ({ user_id }) => {
      setOnlineUsers((prev) => ({ ...prev, [user_id]: false }));
    };

    const handleOnlineStatus = ({ user_id, is_online }) => {
      setOnlineUsers((prev) => ({ ...prev, [user_id]: is_online }));
    };

    const handleNewNotification = (data) => {
      console.log('[Socket] New notification received');
      handleNewNotificationStore(data);
      const notificationType = data?.type || 'notification';
      let soundType = 'notification';
      if (notificationType.includes('match') || notificationType.includes('contact_request')) {
        soundType = 'match';
      } else if (notificationType.includes('admin') || notificationType === 'admin_chat') {
        soundType = 'admin';
      } else if (notificationType.includes('message')) {
        soundType = 'message';
      }
      playNotificationSound(soundType);
      // Browser notification (only when the tab is not visible).
      //
      // Fire-and-forget, and deliberately BEFORE nothing: this used to call
      // `new Notification(...)` inline, which throws `Illegal constructor` on
      // Chrome for Android and took the callback loop below down with it — so a
      // failed notification silently disabled every other consumer of this
      // event (WEFAQNA-FRONTEND-8). showBrowserNotification never throws.
      if (document.visibilityState === 'hidden') {
        showBrowserNotification(data?.title || 'وفّقنا', {
          body: data?.message || '',
          icon: '/logo.png',
          tag: data?.id || 'notification',
        });
      }
      notificationCallbacks.current.forEach((cb) => {
        try { cb(data); } catch (e) { /* silent */ }
      });
    };

    const handleMatchRequest = () => {
      console.log('[Socket] Match request received');
      if (hasAuth) {
        fetchNotifications();
      }
      fireMatchEventCallbacks();
      playNotificationSound('match');
    };

    const handleMatchAccepted = () => {
      console.log('[Socket] Match accepted received');
      if (hasAuth) {
        fetchNotifications();
        fetchConversations();
      }
      fireMatchEventCallbacks();
      playNotificationSound('match');
    };

    const handleMatchRejected = () => {
      console.log('[Socket] Match rejected received');
      if (hasAuth) {
        fetchNotifications();
      }
      fireMatchEventCallbacks();
      playNotificationSound('notification');
    };

    const handleMatchQueued = () => {
      console.log('[Socket] Match queued received');
      if (hasAuth) {
        fetchNotifications();
      }
      fireMatchEventCallbacks();
      playNotificationSound('match');
    };

    const handleConversationActivated = () => {
      console.log('[Socket] Conversation activated from queue');
      if (hasAuth) {
        fetchNotifications();
        fetchConversations();
      }
      fireMatchEventCallbacks();
      playNotificationSound('match');
    };

    const handleMatchRequestCancelled = () => {
      console.log('[Socket] Match request cancelled');
      fireMatchEventCallbacks();
    };

    const handleMatchRequestSent = () => {
      console.log('[Socket] Match request sent confirmation');
      fireMatchEventCallbacks();
    };

    // NEW MESSAGE in conversation room — realtime UI updates only.
    // The store update is handled by new_message_notification.
    const handleNewMessage = (data) => {
      console.log('[Socket] New message received (room event)');
      // The room carries a line written for the other participant to both.
      if (isHiddenFor(data, getCurrentUserId())) return;
      messageCallbacks.current.forEach((cb) => {
        try { cb(data); } catch (e) { /* silent */ }
      });
    };

    // NEW MESSAGE NOTIFICATION to personal room — updates store + sound.
    const handleNewMessageNotification = (data) => {
      console.log('[Socket] New message notification received');
      const currentUserId = getCurrentUserId();
      // Is this message landing in the conversation the user has open?
      const isActiveConversation = data?.conversation_id === activeConversationIdRef.current;
      if (currentUserId) {
        handleNewMessageStore(data, currentUserId);
        // The store bumps unreadCount / unreadByConversation / conv.unread_count
        // for every incoming message. When the message lands in the conversation
        // the user is reading right now, that badge is wrong the moment it
        // appears — so clear the counters straight back with the same action the
        // sidebar-click path uses. last_message, last_message_at and list
        // ordering set by handleNewMessageStore are deliberately left in place;
        // markConversationAsRead only touches the three counter fields.
        // Mirrors the mobile rule that every notification path is gated on the
        // currently-open conversation (_showNotificationIfAllowed).
        if (isActiveConversation && data?.conversation_id) {
          markConversationAsRead(data.conversation_id);
        }
      }
      // Suppress notification sound when the user is already viewing this
      // conversation — pinging them about a message they're actively
      // reading is jarring (and wakes them at night for messages they
      // already saw).
      if (!isActiveConversation) {
        playNotificationSound('message');
      }
    };

    // Something this member sent from another device or tab (BUG-633).
    const handleOwnMessage = (data) => {
      const currentUserId = getCurrentUserId();
      if (currentUserId) handleOwnMessageStore(data, currentUserId);
    };

    const handleMessagesRead = (data) => {
      console.log('[Socket] Messages read receipt received');
      readReceiptCallbacks.current.forEach((cb) => {
        try { cb(data); } catch (e) { /* silent */ }
      });
    };

    const handleAdminChatMessage = () => {
      playNotificationSound('admin');
    };

    const handleAdminChatClosed = () => {
      console.log('[Socket] Admin chat closed event received');
      if (hasAuth) {
        fetchConversations();
      }
    };

    const handleConversationClosed = (data) => {
      console.log('[Socket] Conversation closed event received');
      if (data?.conversation_id) {
        removeConversation(data.conversation_id);
        if (hasAuth) {
          fetchConversations();
        }
      }
    };

    const handleMessageEdited = (data) => {
      console.log('[Socket] Message edited event received');
      messageEditedCallbacks.current.forEach((cb) => {
        try { cb(data); } catch (e) { /* silent */ }
      });
    };

    const handleMessageDeleted = (data) => {
      console.log('[Socket] Message deleted event received');
      messageDeletedCallbacks.current.forEach((cb) => {
        try { cb(data); } catch (e) { /* silent */ }
      });
    };

    const handleMessageReaction = (data) => {
      console.log('[Socket] Message reaction event received');
      messageReactionCallbacks.current.forEach((cb) => {
        try { cb(data); } catch (e) { /* silent */ }
      });
    };

    // Scheduled audio call ended — pushed to both participants when
    // either side reports/exits/times out. Lets CallScreen exit
    // immediately on the OTHER side, rather than waiting for LiveKit's
    // unreliable disconnect propagation.
    const handleCallEnded = (data) => {
      console.log('[Socket] call_ended event received', data);
      callEndedCallbacks.current.forEach((cb) => {
        try { cb(data); } catch (e) { /* silent */ }
      });
    };

    // Scheduled call status transition (e.g. confirmed → in_progress
    // when one side mints a token / joins the room). Fired to both
    // participants so the OTHER side's call card refreshes in real time.
    const handleCallStatusChanged = (data) => {
      console.log('[Socket] call_status_changed event received', data);
      callStatusChangedCallbacks.current.forEach((cb) => {
        try { cb(data); } catch (e) { /* silent */ }
      });
    };

    // Real-time activity refresh — backend emits this on every send_message
    // so the receiver's chat header shows accurate last_seen instantly.
    const handleUserActivityUpdate = (data) => {
      const userId = data?.user_id;
      if (!userId) return;
      setUserActivity((prev) => ({
        ...prev,
        [userId]: {
          is_online: data?.is_online !== false,
          last_seen: data?.last_seen ?? null,
        },
      }));
    };

    const handleForceLogout = (data) => {
      safeLocalStorage.removeItem('wefaqna_token');
      safeLocalStorage.removeItem('authToken');
      safeLocalStorage.removeItem('user');
      safeLocalStorage.removeItem('adminUser');
      safeLocalStorage.removeItem('wefaqna_offline_messages');
      resetMessagesStore();
      resetNotificationsStore();
      // Replaced blocking alert() with non-blocking toast — alert() blocks
      // the JS thread and renders inconsistently in some mobile browsers.
      try {
        if (typeof window !== 'undefined' && window.toast) {
          window.toast(data.message || authError('loggedOut'));
        } else {
          console.warn('[force_logout]', data.message || authError('loggedOut'));
        }
      } catch (_) { /* silent */ }
      if (globalLogoutFn) globalLogoutFn();
      window.location.href = '/login';
    };

    // The backend answers a rejected handshake/auth with an `error` event
    // ('Token expired', 'No token provided', 'Authentication failed',
    // 'Unauthorized') and deliberately does NOT disconnect — the socket stays
    // connected but ANONYMOUS, receiving zero real-time events. Web already
    // recovers (handleConnect re-emits `authenticate`, Effect 3 re-emits on
    // token rotation); what was missing was any way to SEE that it happened.
    // Observability only — auth behaviour is unchanged.
    const handleAuthError = (data) => {
      const message = typeof data === 'string' ? data : data?.message;
      console.warn('[Socket] Auth rejected:', message || data);
    };

    // ----- Bind every event to its named handler -----
    socket.on('connect', handleConnect);
    socket.on('error', handleAuthError);
    socket.on('authenticated', handleAuthenticated);
    socket.on('disconnect', handleDisconnect);
    socket.on('reconnect_attempt', handleReconnectAttempt);
    socket.on('reconnect', handleReconnect);
    socket.on('reconnect_failed', handleReconnectFailed);
    socket.on('connect_error', handleConnectError);
    socket.on('user_typing', handleUserTyping);
    socket.on('user_stopped_typing', handleUserStoppedTyping);
    socket.on('user_online', handleUserOnline);
    socket.on('user_offline', handleUserOffline);
    socket.on('online_status', handleOnlineStatus);
    socket.on('new_notification', handleNewNotification);
    socket.on('match_request', handleMatchRequest);
    socket.on('match_accepted', handleMatchAccepted);
    socket.on('match_rejected', handleMatchRejected);
    socket.on('match_queued', handleMatchQueued);
    socket.on('conversation_activated', handleConversationActivated);
    socket.on('match_request_cancelled', handleMatchRequestCancelled);
    socket.on('match_request_sent', handleMatchRequestSent);
    socket.on('new_message', handleNewMessage);
    socket.on('new_message_notification', handleNewMessageNotification);
    socket.on('own_message', handleOwnMessage);
    socket.on('messages_read', handleMessagesRead);
    socket.on('admin_chat_message', handleAdminChatMessage);
    socket.on('admin_chat_closed', handleAdminChatClosed);
    socket.on('conversation_closed', handleConversationClosed);
    socket.on('message_edited', handleMessageEdited);
    socket.on('message_deleted', handleMessageDeleted);
    socket.on('message_reaction', handleMessageReaction);
    socket.on('call_ended', handleCallEnded);
    socket.on('call_status_changed', handleCallStatusChanged);
    socket.on('user_activity_update', handleUserActivityUpdate);
    socket.on('force_logout', handleForceLogout);

    // ----- Symmetric cleanup: each off() targets ONLY this run's handler.
    return () => {
      socket.off('connect', handleConnect);
      socket.off('error', handleAuthError);
      socket.off('authenticated', handleAuthenticated);
      socket.off('disconnect', handleDisconnect);
      socket.off('reconnect_attempt', handleReconnectAttempt);
      socket.off('reconnect', handleReconnect);
      socket.off('reconnect_failed', handleReconnectFailed);
      socket.off('connect_error', handleConnectError);
      socket.off('user_typing', handleUserTyping);
      socket.off('user_stopped_typing', handleUserStoppedTyping);
      socket.off('user_online', handleUserOnline);
      socket.off('user_offline', handleUserOffline);
      socket.off('online_status', handleOnlineStatus);
      socket.off('new_notification', handleNewNotification);
      socket.off('match_request', handleMatchRequest);
      socket.off('match_accepted', handleMatchAccepted);
      socket.off('match_rejected', handleMatchRejected);
      socket.off('match_queued', handleMatchQueued);
      socket.off('conversation_activated', handleConversationActivated);
      socket.off('match_request_cancelled', handleMatchRequestCancelled);
      socket.off('match_request_sent', handleMatchRequestSent);
      socket.off('new_message', handleNewMessage);
      socket.off('new_message_notification', handleNewMessageNotification);
      socket.off('own_message', handleOwnMessage);
      socket.off('messages_read', handleMessagesRead);
      socket.off('admin_chat_message', handleAdminChatMessage);
      socket.off('admin_chat_closed', handleAdminChatClosed);
      socket.off('conversation_closed', handleConversationClosed);
      socket.off('message_edited', handleMessageEdited);
      socket.off('message_deleted', handleMessageDeleted);
      socket.off('message_reaction', handleMessageReaction);
      socket.off('call_ended', handleCallEnded);
      socket.off('call_status_changed', handleCallStatusChanged);
      socket.off('user_activity_update', handleUserActivityUpdate);
      socket.off('force_logout', handleForceLogout);
    };
  }, [socketCreatedTick, hasAuth, getActiveToken, getCurrentUserId, fetchAllData, handleNewMessageStore, handleOwnMessageStore, handleNewNotificationStore, removeConversation, markConversationAsRead, fetchConversations, fetchNotifications, resetMessagesStore, resetNotificationsStore, fireMatchEventCallbacks]);

  // ============================================================
  // EFFECT 3 — Token rotation
  //
  // When the auth token silently rotates (~25-55 min) and the socket is
  // already connected, just re-emit `authenticate` to prove our new
  // identity to the server. NO socket rebuild, NO listener rebind.
  // The first authenticate after handshake is handled inside
  // handleConnect (Effect 2).
  // ============================================================
  useEffect(() => {
    if (socketRef.current?.connected && token) {
      socketRef.current.emit('authenticate', { token });
    }
  }, [token]);

  // Initial data fetch when authenticated
  useEffect(() => {
    if (hasAuth) {
      fetchAllData();
    }
  }, [hasAuth, fetchAllData]);

  // Subscribe to notifications (legacy API for backwards compatibility)
  const onNotification = useCallback((callback) => {
    notificationCallbacks.current.push(callback);
    return () => {
      notificationCallbacks.current = notificationCallbacks.current.filter(cb => cb !== callback);
    };
  }, []);

  // Subscribe to new messages (legacy API for backwards compatibility)
  const onNewMessage = useCallback((callback) => {
    messageCallbacks.current.push(callback);
    return () => {
      messageCallbacks.current = messageCallbacks.current.filter(cb => cb !== callback);
    };
  }, []);

  // Subscribe to match events (request created/cancelled/accepted/rejected)
  const onMatchEvent = useCallback((callback) => {
    matchEventCallbacks.current.add(callback);
    return () => {
      matchEventCallbacks.current.delete(callback);
    };
  }, []);

  // Subscribe to read receipts (sender gets notified when receiver reads)
  const onReadReceipt = useCallback((callback) => {
    readReceiptCallbacks.current.push(callback);
    return () => {
      readReceiptCallbacks.current = readReceiptCallbacks.current.filter(cb => cb !== callback);
    };
  }, []);

  // Subscribe to message edited events (real-time from other user)
  const onMessageEdited = useCallback((callback) => {
    messageEditedCallbacks.current.push(callback);
    return () => {
      messageEditedCallbacks.current = messageEditedCallbacks.current.filter(cb => cb !== callback);
    };
  }, []);

  // Subscribe to call_ended events (real-time call termination)
  const onCallEnded = useCallback((callback) => {
    callEndedCallbacks.current.push(callback);
    return () => {
      callEndedCallbacks.current = callEndedCallbacks.current.filter(cb => cb !== callback);
    };
  }, []);

  // Subscribe to call_status_changed events (real-time mid-call state
  // transitions, e.g. peer joined the LiveKit room).
  const onCallStatusChanged = useCallback((callback) => {
    callStatusChangedCallbacks.current.push(callback);
    return () => {
      callStatusChangedCallbacks.current = callStatusChangedCallbacks.current.filter(cb => cb !== callback);
    };
  }, []);

  // Subscribe to message deleted events (real-time from other user)
  const onMessageDeleted = useCallback((callback) => {
    messageDeletedCallbacks.current.push(callback);
    return () => {
      messageDeletedCallbacks.current = messageDeletedCallbacks.current.filter(cb => cb !== callback);
    };
  }, []);

  // Subscribe to message reaction events (real-time from other user)
  const onMessageReaction = useCallback((callback) => {
    messageReactionCallbacks.current.push(callback);
    return () => {
      messageReactionCallbacks.current = messageReactionCallbacks.current.filter(cb => cb !== callback);
    };
  }, []);

  // Join a conversation room
  const joinConversation = useCallback((conversationId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join_conversation', { conversation_id: conversationId });
    }
  }, []);

  // Leave a conversation room
  const leaveConversation = useCallback((conversationId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_conversation', { conversation_id: conversationId });
    }
  }, []);

  // Send typing indicator
  const sendTyping = useCallback((conversationId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing', { conversation_id: conversationId });
    }
  }, []);

  // Stop typing indicator
  const stopTyping = useCallback((conversationId) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('stop_typing', { conversation_id: conversationId });
    }
  }, []);

  // Check if a user is online
  const checkOnline = useCallback((userId) => {
    if (socketRef.current?.connected && userId) {
      socketRef.current.emit('check_online', { user_id: userId });
    }
  }, []);

  // Check if user is online from cache
  const isUserOnline = useCallback((userId) => {
    return onlineUsers[userId] === true;
  }, [onlineUsers]);

  // Join admin room
  const joinAdminRoom = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('join_admin_room');
    }
  }, []);

  // Leave admin room
  const leaveAdminRoom = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('leave_admin_room');
    }
  }, []);

  // Track active conversation for notification suppression
  const setActiveConversation = useCallback((conversationId) => {
    activeConversationIdRef.current = conversationId;
  }, []);

  // Manual refresh function (for components to call)
  const refreshData = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  const value = {
    socket: socketRef.current,
    connected,
    authenticated,
    connectionStatus,
    joinConversation,
    leaveConversation,
    sendTyping,
    stopTyping,
    onNewMessage,
    onReadReceipt,
    onMessageEdited,
    onMessageDeleted,
    onMessageReaction,
    onCallEnded,
    onCallStatusChanged,
    onNotification,
    onMatchEvent,
    typingUsers,
    onlineUsers,
    userActivity, // { user_id: { is_online, last_seen } } — live activity feed
    checkOnline,
    isUserOnline,
    joinAdminRoom,
    leaveAdminRoom,
    reconnect,
    refreshData,
    setActiveConversation,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

export default SocketContext;
